import Joi, { ValidationResult } from 'joi';

import { LanguageType } from '@lifeserver/shared';

import { Media } from '../types/mediaTypes';
import { CreateOrUpdateTag } from '../types/tagTypes';

import { getLocalizedFieldSchema } from '../banners/validate';

const languages = Object.values(LanguageType);

const createSchemas = () => {
    const tagErrorSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            stringId: Joi.string().required().max(64).messages({
                'string.base': 'String ID must be a string',
                'string.empty': 'String ID is missing',
                'any.required': 'String ID is missing',
                'string.max': 'String ID must not exceed 64 characters',
            }),
            name: Joi.object()
                .keys(
                    getLocalizedFieldSchema(languages, (lang) => {
                        if (lang === LanguageType.English) {
                            return Joi.string()
                                .required()
                                .messages({
                                    'string.base': `"${lang}" for name must be a type of string`,
                                    'string.empty': `"${lang}" for name is missing`,
                                    'any.required': `"${lang}" for name is missing`,
                                });
                        }

                        return Joi.string()
                            .optional()
                            .allow('')
                            .messages({
                                'string.base': `"${lang}" for name must be a type of string`,
                                'string.empty': `"${lang}" for name is missing`,
                                'any.required': `"${lang}" for name is missing`,
                            });
                    }),
                )
                .required(),
            parents: Joi.array().items(Joi.number().integer()).required(),
            children: Joi.array().items(Joi.number().integer()).required(),
            synonyms: Joi.array()
                .items(
                    Joi.string()
                        .pattern(/^[a-zA-Z-]+$/)
                        .messages({
                            'string.base': 'Each synonym must be a string',
                            'string.empty':
                                'Synonyms cannot contain empty values',
                            'string.pattern.base':
                                'Each synonym must contain only letters and dashes',
                        }),
                )
                .required(),
            iconMediaId: Joi.number().integer().allow(null).optional(),
            iconMediaStatus: Joi.any(),
        });

    const tagWarningSchema = tagErrorSchema.keys({
        name: Joi.object()
            .keys(
                getLocalizedFieldSchema(languages, (lang) =>
                    Joi.string()
                        .required()
                        .allow('')
                        .messages({
                            'string.base': `"${lang}" for name must be a type of string`,
                            'string.empty': `"${lang}" for name is missing`,
                            'any.required': `"${lang}" for name is missing`,
                        }),
                ),
            )
            .required(),
        parents: Joi.array().items(Joi.number().integer()).required(),
        children: Joi.array().items(Joi.number().integer()).required(),
        iconMediaStatus: Joi.valid('completed', null).messages({
            'any.only':
                'Tag icon is still processing or failed to process. Players see a broken image until it is ready.',
        }),
    });

    return {
        tagErrorSchema: tagErrorSchema
            .custom((value, helpers) => {
                if (value.parents.length > 0 && value.children.length > 0) {
                    return helpers.error('any.invalid');
                }

                return value;
            }, 'Mutually exclusive tag relations')
            .messages({
                'any.invalid':
                    'A tag can have parents or children, but not both',
            }),
        tagWarningSchema,
    };
};

export default function validate(
    item: CreateOrUpdateTag,
    { iconMediaStatus }: { iconMediaStatus?: Media['processingStatus'] } = {},
): {
    errors: ValidationResult;
    warnings: ValidationResult;
} {
    const { tagErrorSchema, tagWarningSchema } = createSchemas();
    const validationItem = { ...item, iconMediaStatus };

    return {
        errors: tagErrorSchema.validate(validationItem),
        warnings: tagWarningSchema.validate(validationItem),
    };
}
